import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useRef, useState } from 'react';
import type { AssistantEvent } from '@accounting/shared';
import { PROVIDER_LABELS, useCalendars, useConnections, useEvents } from '../../hooks/useAssistant';
import { useCreateEvent, useDeleteEvent, useUpdateEvent } from '../../hooks/useAssistantTasks';
import { EventForm } from '../../components/assistant/EventForm';
import { ApiError } from '../../lib/api';
import {
  addDaysLocal,
  agendaRange,
  bucketByDay,
  dayHeading,
  daysInRange,
  formatTimeRange,
  formatTimeShort,
  layoutTimedDay,
  localDateKey,
  monthGridRange,
  monthLabel,
  rangeLabel,
  startOfLocalDay,
  weekRange,
  type DateRange,
} from '../../lib/calendarGrid';

export const Route = createFileRoute('/_auth/assistant/calendar')({ component: CalendarPage });

type View = 'week' | 'month' | 'agenda';
const HOUR_PX = 48;
const SNAP_MIN = 15;
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type FormState = { mode: 'create'; anchor: Date } | { mode: 'edit'; event: AssistantEvent } | null;

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | null;
    return body?.error ?? `Request failed (${err.status})`;
  }
  return err instanceof Error ? err.message : String(err);
}

/** Shift an instant by whole local days (keeps wall-clock time across DST) and minutes. */
function shift(iso: string, days: number, minutes: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  d.setMinutes(d.getMinutes() + minutes);
  return d.toISOString();
}

function CalendarPage() {
  const [view, setView] = useState<View>('week');
  const [anchor, setAnchor] = useState<Date>(() => startOfLocalDay(new Date()));
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const [selected, setSelected] = useState<AssistantEvent | null>(null);
  const [form, setForm] = useState<FormState>(null);
  const [message, setMessage] = useState<string | null>(null);

  const range: DateRange = useMemo(() => {
    if (view === 'week') return weekRange(anchor);
    if (view === 'month') return monthGridRange(anchor.getFullYear(), anchor.getMonth());
    return agendaRange(anchor, 14);
  }, [view, anchor]);

  const connections = useConnections();
  const calendars = useCalendars();
  const visibleConnectionIds = useMemo(
    () => (connections.data ?? []).filter((c) => !hidden.has(c.id)).map((c) => c.id),
    [connections.data, hidden],
  );
  const events = useEvents(localDateKey(range.from), localDateKey(range.to), visibleConnectionIds);
  const byDay = useMemo(() => bucketByDay(events.data ?? []), [events.data]);

  const create = useCreateEvent();
  const update = useUpdateEvent();
  const remove = useDeleteEvent();
  const busy = create.isPending || update.isPending || remove.isPending;

  const step = (dir: -1 | 1) => {
    if (view === 'week') setAnchor(addDaysLocal(anchor, 7 * dir));
    else if (view === 'month') setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1));
    else setAnchor(addDaysLocal(anchor, 14 * dir));
  };

  const moveEvent = (ev: AssistantEvent, days: number, minutes: number) => {
    update.mutate(
      { id: ev.id, start: shift(ev.startsAt, days, minutes), end: shift(ev.endsAt, days, minutes) },
      { onError: (err) => setMessage(errorMessage(err)) },
    );
  };

  const deleteEvent = (ev: AssistantEvent) => {
    if (!window.confirm(`Delete "${ev.subject || '(no title)'}" from ${PROVIDER_LABELS[ev.provider] ?? ev.provider}?`)) return;
    remove.mutate(ev.id, {
      onSuccess: () => setSelected(null),
      onError: (err) => setMessage(errorMessage(err)),
    });
  };

  const noConnections = connections.data && connections.data.length === 0;
  const hasWritable = (calendars.data ?? []).some((c) => c.canWrite && c.isSelected);
  const title = view === 'month' ? monthLabel(anchor.getFullYear(), anchor.getMonth()) : rangeLabel(range);

  return (
    <div className="pa-calendar-page">
      <header className="pa-toolbar">
        <div className="pa-toolbar__nav">
          <button type="button" className="btn-secondary" onClick={() => step(-1)} aria-label="Previous">‹</button>
          <button type="button" className="btn-secondary" onClick={() => setAnchor(startOfLocalDay(new Date()))}>Today</button>
          <button type="button" className="btn-secondary" onClick={() => step(1)} aria-label="Next">›</button>
          <h1 style={{ margin: '0 0 0 8px', fontSize: 20 }}>{title}</h1>
          {(events.isFetching || busy) && <span className="muted" style={{ fontSize: 12 }}>{busy ? 'saving…' : 'updating…'}</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="pa-toolbar__views" role="tablist">
            {(['week', 'month', 'agenda'] as View[]).map((v) => (
              <button key={v} type="button" role="tab" aria-selected={view === v} className={`btn-secondary${view === v ? ' pa-view--active' : ''}`} onClick={() => setView(v)}>
                {v[0]!.toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
          <button type="button" className="btn-primary" disabled={!hasWritable} title={hasWritable ? undefined : 'No writable calendar is connected'} onClick={() => setForm({ mode: 'create', anchor })}>
            New event
          </button>
        </div>
      </header>

      {message && (
        <div className="card pa-banner pa-banner--err" role="alert">
          <span>{message}</span>
          <button type="button" className="link-button" onClick={() => setMessage(null)}>Dismiss</button>
        </div>
      )}

      {connections.data && connections.data.length > 0 && (
        <div className="pa-chips">
          {connections.data.map((c) => {
            const off = hidden.has(c.id);
            return (
              <button
                key={c.id}
                type="button"
                className={`pa-chip${off ? ' pa-chip--off' : ''}`}
                aria-pressed={!off}
                onClick={() =>
                  setHidden((prev) => {
                    const next = new Set(prev);
                    if (next.has(c.id)) next.delete(c.id);
                    else next.add(c.id);
                    return next;
                  })
                }
                title={`${PROVIDER_LABELS[c.provider] ?? c.provider} · ${c.accountEmail ?? ''}`}
              >
                <span className="pa-dot" style={{ background: c.color }} aria-hidden="true" />
                {c.label}
                {c.status === 'needs_reauth' && <span className="pa-chip__warn" title="Needs sign-in">!</span>}
              </button>
            );
          })}
        </div>
      )}

      {noConnections && (
        <div className="card" style={{ marginTop: 12 }}>
          <p style={{ margin: 0 }}>
            No accounts connected yet. <Link to="/assistant/connections">Connect a calendar</Link> to see it here.
          </p>
        </div>
      )}

      {events.isError && (
        <div className="card pa-banner pa-banner--err" style={{ marginTop: 12 }}>Could not load events. Try again in a moment.</div>
      )}

      {view === 'week' && <WeekView range={range} byDay={byDay} onPick={setSelected} onMove={moveEvent} onCreateAt={(d) => setForm({ mode: 'create', anchor: d })} />}
      {view === 'month' && <MonthView range={range} anchor={anchor} byDay={byDay} onPick={setSelected} onDay={(d) => { setAnchor(d); setView('week'); }} />}
      {view === 'agenda' && <AgendaView range={range} byDay={byDay} onPick={setSelected} />}

      {selected && (
        <EventDetail
          event={selected}
          busy={busy}
          onClose={() => setSelected(null)}
          onEdit={() => {
            setForm({ mode: 'edit', event: selected });
            setSelected(null);
          }}
          onDelete={() => deleteEvent(selected)}
        />
      )}

      {form && (
        <EventForm
          calendars={calendars.data ?? []}
          event={form.mode === 'edit' ? form.event : null}
          anchor={form.mode === 'create' ? form.anchor : undefined}
          busy={busy}
          error={create.isError ? errorMessage(create.error) : update.isError ? errorMessage(update.error) : null}
          onCreate={(input) => create.mutate(input, { onSuccess: () => setForm(null) })}
          onUpdate={(id, patch) => update.mutate({ id, ...patch }, { onSuccess: () => setForm(null) })}
          onClose={() => setForm(null)}
        />
      )}
    </div>
  );
}

function EventChip({ ev, onPick, showTime }: { ev: AssistantEvent; onPick: (e: AssistantEvent) => void; showTime?: boolean }) {
  return (
    <button
      type="button"
      className={`pa-event-chip${ev.isAllDay ? ' pa-event-chip--allday' : ''}${ev.status === 'tentative' ? ' pa-event-chip--tentative' : ''}`}
      style={{ '--ev': ev.color } as React.CSSProperties}
      onClick={() => onPick(ev)}
      title={ev.subject}
    >
      {showTime && !ev.isAllDay && <span className="pa-event-chip__time">{formatTimeShort(ev.startsAt)}</span>}
      <span className="pa-event-chip__subject">{ev.subject || '(no title)'}</span>
    </button>
  );
}

interface DragState {
  id: string;
  dx: number;
  dy: number;
}

function WeekView({
  range,
  byDay,
  onPick,
  onMove,
  onCreateAt,
}: {
  range: DateRange;
  byDay: Map<string, AssistantEvent[]>;
  onPick: (e: AssistantEvent) => void;
  onMove: (e: AssistantEvent, days: number, minutes: number) => void;
  onCreateAt: (d: Date) => void;
}) {
  const days = daysInRange(range);
  const todayKey = localDateKey(new Date());
  const hours = Array.from({ length: 24 }, (_, h) => h);
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const [drag, setDrag] = useState<DragState | null>(null);
  const dragRef = useRef<{ ev: AssistantEvent; startX: number; startY: number; colWidth: number } | null>(null);

  const onPointerDown = (ev: AssistantEvent) => (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    const col = e.currentTarget.parentElement;
    dragRef.current = { ev, startX: e.clientX, startY: e.clientY, colWidth: col?.getBoundingClientRect().width ?? 1 };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const d = dragRef.current;
    if (!d) return;
    setDrag({ id: d.ev.id, dx: e.clientX - d.startX, dy: e.clientY - d.startY });
  };
  const onPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const d = dragRef.current;
    dragRef.current = null;
    setDrag(null);
    if (!d) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    const dayDelta = Math.round(dx / d.colWidth);
    const minDelta = Math.round(((dy / HOUR_PX) * 60) / SNAP_MIN) * SNAP_MIN;
    if (d.ev.canWrite && (dayDelta !== 0 || minDelta !== 0) && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) onMove(d.ev, dayDelta, minDelta);
    else onPick(d.ev);
  };

  return (
    <div className="card pa-week">
      <div className="pa-week__header">
        <div className="pa-week__gutter" />
        {days.map((d) => {
          const key = localDateKey(d);
          return (
            <div key={key} className={`pa-week__dayhead${key === todayKey ? ' pa-today' : ''}`}>
              <div className="muted" style={{ fontSize: 12 }}>{DAY_NAMES[d.getDay()]}</div>
              <div className="pa-week__daynum">{d.getDate()}</div>
            </div>
          );
        })}
      </div>
      <div className="pa-week__allday">
        <div className="pa-week__gutter muted" style={{ fontSize: 11 }}>all-day</div>
        {days.map((d) => {
          const key = localDateKey(d);
          const list = (byDay.get(key) ?? []).filter((e) => e.isAllDay);
          return (
            <div key={key} className="pa-week__alldaycell">
              {list.map((ev) => (
                <EventChip key={ev.id} ev={ev} onPick={onPick} />
              ))}
            </div>
          );
        })}
      </div>
      <div className="pa-week__scroll">
        <div className="pa-week__grid" style={{ height: 24 * HOUR_PX }}>
          <div className="pa-week__gutter pa-week__hours">
            {hours.map((h) => (
              <div key={h} className="pa-week__hour" style={{ top: h * HOUR_PX }}>
                {h === 0 ? '' : `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? 'a' : 'p'}`}
              </div>
            ))}
          </div>
          {days.map((d) => {
            const key = localDateKey(d);
            const placed = layoutTimedDay(key, byDay.get(key) ?? []);
            return (
              <div
                key={key}
                className={`pa-week__col${key === todayKey ? ' pa-today' : ''}`}
                onDoubleClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const minutes = Math.floor(((e.clientY - rect.top) / HOUR_PX) * 60 / 30) * 30;
                  const at = new Date(d);
                  at.setMinutes(minutes);
                  onCreateAt(at);
                }}
                title="Double-click to add an event"
              >
                {hours.map((h) => (
                  <div key={h} className="pa-week__line" style={{ top: h * HOUR_PX }} />
                ))}
                {key === todayKey && <div className="pa-week__now" style={{ top: (nowMin / 60) * HOUR_PX }} />}
                {placed.map((p) => {
                  const dragging = drag?.id === p.event.id;
                  return (
                    <button
                      key={p.event.id}
                      type="button"
                      className={`pa-event-block${p.event.status === 'tentative' ? ' pa-event-block--tentative' : ''}${p.event.canWrite ? ' pa-event-block--movable' : ''}${dragging ? ' pa-event-block--dragging' : ''}${p.event.pendingSync ? ' pa-event-block--pending' : ''}`}
                      style={{
                        '--ev': p.event.color,
                        top: (p.top / 60) * HOUR_PX,
                        height: Math.max((p.height / 60) * HOUR_PX, 18),
                        left: `calc(${(p.lane / p.lanes) * 100}% + 2px)`,
                        width: `calc(${100 / p.lanes}% - 4px)`,
                        transform: dragging ? `translate(${drag!.dx}px, ${Math.round(drag!.dy / ((HOUR_PX / 60) * SNAP_MIN)) * (HOUR_PX / 60) * SNAP_MIN}px)` : undefined,
                      } as React.CSSProperties}
                      onPointerDown={onPointerDown(p.event)}
                      onPointerMove={onPointerMove}
                      onPointerUp={onPointerUp}
                      onPointerCancel={() => {
                        dragRef.current = null;
                        setDrag(null);
                      }}
                      title={`${p.event.subject} · ${formatTimeRange(p.event.startsAt, p.event.endsAt)}${p.event.canWrite ? ' — drag to move' : ''}`}
                    >
                      <span className="pa-event-block__time">{formatTimeShort(p.event.startsAt)}</span>
                      <span className="pa-event-block__subject">{p.event.subject || '(no title)'}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MonthView({
  range,
  anchor,
  byDay,
  onPick,
  onDay,
}: {
  range: DateRange;
  anchor: Date;
  byDay: Map<string, AssistantEvent[]>;
  onPick: (e: AssistantEvent) => void;
  onDay: (d: Date) => void;
}) {
  const days = daysInRange(range);
  const todayKey = localDateKey(new Date());
  const MAX = 4;
  return (
    <div className="card pa-month">
      <div className="pa-month__header">
        {DAY_NAMES.map((n) => (
          <div key={n} className="muted" style={{ fontSize: 12, textAlign: 'center' }}>{n}</div>
        ))}
      </div>
      <div className="pa-month__grid">
        {days.map((d) => {
          const key = localDateKey(d);
          const list = byDay.get(key) ?? [];
          const outside = d.getMonth() !== anchor.getMonth();
          return (
            <div key={key} className={`pa-month__cell${outside ? ' pa-month__cell--outside' : ''}${key === todayKey ? ' pa-today' : ''}`}>
              <button type="button" className="pa-month__daynum link-button" onClick={() => onDay(d)}>{d.getDate()}</button>
              {list.slice(0, MAX).map((ev) => (
                <EventChip key={ev.id} ev={ev} onPick={onPick} showTime />
              ))}
              {list.length > MAX && (
                <button type="button" className="link-button pa-month__more" onClick={() => onDay(d)}>+{list.length - MAX} more</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AgendaView({ range, byDay, onPick }: { range: DateRange; byDay: Map<string, AssistantEvent[]>; onPick: (e: AssistantEvent) => void }) {
  const days = daysInRange(range);
  const todayKey = localDateKey(new Date());
  const nonEmpty = days.filter((d) => (byDay.get(localDateKey(d)) ?? []).length > 0);
  if (nonEmpty.length === 0) {
    return (
      <div className="card" style={{ marginTop: 12 }}>
        <p className="muted" style={{ margin: 0 }}>Nothing scheduled in this window.</p>
      </div>
    );
  }
  return (
    <div className="pa-agenda">
      {nonEmpty.map((d) => {
        const key = localDateKey(d);
        return (
          <div key={key} className={`card pa-agenda__day${key === todayKey ? ' pa-today' : ''}`}>
            <h3 className="pa-agenda__heading">{key === todayKey ? `Today · ${dayHeading(d)}` : dayHeading(d)}</h3>
            {(byDay.get(key) ?? []).map((ev) => (
              <button key={ev.id} type="button" className="pa-agenda__row" onClick={() => onPick(ev)}>
                <span className="pa-dot" style={{ background: ev.color }} aria-hidden="true" />
                <span className="pa-agenda__time">{ev.isAllDay ? 'all day' : formatTimeRange(ev.startsAt, ev.endsAt)}</span>
                <span className="pa-agenda__subject">{ev.subject || '(no title)'}</span>
                {ev.location && <span className="muted pa-agenda__loc">{ev.location}</span>}
                {ev.matterRef && <span className="tag">{ev.matterRef}</span>}
              </button>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function EventDetail({
  event: ev,
  busy,
  onClose,
  onEdit,
  onDelete,
}: {
  event: AssistantEvent;
  busy: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const start = new Date(ev.startsAt);
  const multiDay = ev.isAllDay && ev.startDate && ev.endDate && ev.endDate > nextDay(ev.startDate);
  const whenText = ev.isAllDay
    ? `${ev.startDate}${multiDay ? ` → ${ev.endDate} (excl.)` : ''} · all day`
    : `${start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · ${formatTimeRange(ev.startsAt, ev.endsAt)}`;
  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div className="modal pa-detail" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={ev.subject}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="pa-dot" style={{ background: ev.color }} aria-hidden="true" />
          <h2 style={{ margin: 0, fontSize: 18, flex: 1 }}>{ev.subject || '(no title)'}</h2>
          <button type="button" className="link-button" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <p style={{ margin: '8px 0 0' }}>{whenText}</p>
        {ev.location && <p className="muted" style={{ margin: '4px 0 0' }}>{ev.location}</p>}
        <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
          {PROVIDER_LABELS[ev.provider] ?? ev.provider}
          {ev.matterRef ? ` · matter ${ev.matterRef}` : ''}
          {ev.status !== 'confirmed' ? ` · ${ev.status}` : ''}
          {ev.pendingSync ? ' · waiting for sync' : ''}
          {!ev.canWrite ? ' · read-only' : ''}
        </p>
        {ev.attendees.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>Attendees</div>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {ev.attendees.map((a, i) => (
                <li key={i}>
                  {a.name ?? a.email ?? '—'}
                  {a.status && a.status !== 'none' ? <span className="muted"> · {a.status}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        )}
        {ev.bodyPreview && <p style={{ marginTop: 10, whiteSpace: 'pre-wrap', fontSize: 13 }}>{ev.bodyPreview}</p>}
        <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center' }}>
          {ev.canWrite && (
            <>
              <button type="button" className="btn-secondary" onClick={onEdit} disabled={busy}>Edit</button>
              <button type="button" className="btn-danger" onClick={onDelete} disabled={busy}>Delete</button>
            </>
          )}
          {ev.webLink && (
            <a href={ev.webLink} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 'auto' }}>
              Open in {PROVIDER_LABELS[ev.provider] ?? ev.provider}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function nextDay(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  return localDateKey(new Date(y!, m! - 1, d! + 1));
}
